import { traceEvent } from '@wk/wk-gatherer';
import { Button } from '@wk/wk-uni-lib';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useContext } from 'react';
import { Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import LeadButton from '@/components/LeadButton';
import { CaseCard, MobileCaseCard } from '@/components/WukongReviews/CaseCard';
import { GlobalLoginContext } from '@/context/GlobalLoginContest';

import { studentCases } from '../data';

import 'swiper/css';
import 'swiper/css/pagination';

// 自定义 Swiper pagination 样式
const swiperStyles = `
  .case-swiper .swiper-pagination-bullet {
    background: #C6C7D2;
    opacity: 1;
  }
  .case-swiper .swiper-pagination-bullet-active {
    background: #0a0a0a;
  }
`;

/**
 * 案例区模块
 * 展示真实学生故事（默认展示全部案例，不再折叠）
 */
const CaseSection: React.FC = () => {
  const { t, i18n } = useTranslation('wukong-reviews');
  const isZh = i18n.language === 'zh';
  const { isLogin } = useContext(GlobalLoginContext);

  // 约课按钮埋点
  const handleBookClassTrace = useCallback(() => {
    traceEvent({
      data: {
        _event: 'C_WallofLove_BookClass',
        dsSource: '用户之声-详细案例',
        loginStatus: isLogin ? '已登' : '未登',
      },
    });
  }, [isLogin]);

  // 注入自定义样式
  React.useEffect(() => {
    const styleId = 'case-swiper-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = swiperStyles;
      document.head.appendChild(style);
    }
    return () => {
      const style = document.getElementById(styleId);
      if (style) {
        style.remove();
      }
    };
  }, []);

  return (
    <section className="flex w-full flex-col items-center px-4 py-0 lg:px-8">
      {/* 标题 */}
      <h2
        className={`mb-6 text-center text-[28px] font-black leading-normal text-text-primary lg:mb-12 lg:mt-6 lg:text-[34px] xl:mb-15 xl:mt-8 xl:text-[48px] ${isZh ? 'font-sourceHanSans' : ''}`}>
        <span
          className="[&>span]:text-brand-red-100"
          dangerouslySetInnerHTML={{ __html: t('案例-标题') }}
        />
        <span className="hidden lg:inline"> ❤️</span>
      </h2>

      {/* PC 端 - 卡片列表（默认展示全部） */}
      <div className="hidden w-full max-w-[984px] flex-col lg:flex lg:space-y-8 xl:max-w-[1320px] xl:space-y-10">
        {studentCases.map(caseItem => (
          <CaseCard
            key={caseItem.id}
            caseId={caseItem.id}
            image={caseItem.image}
            rating={caseItem.rating}
          />
        ))}
      </div>

      {/* 移动端 - Swiper 轮播 */}
      <div className="w-full lg:hidden">
        <Swiper
          modules={[Pagination]}
          pagination={{ clickable: true }}
          slidesPerView={1}
          spaceBetween={0}
          className="case-swiper !pb-10">
          {studentCases.map(caseItem => (
            <SwiperSlide key={caseItem.id} className="!h-auto px-px">
              <MobileCaseCard
                caseId={caseItem.id}
                image={caseItem.image}
                rating={caseItem.rating}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* CTA 按钮 */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div onClick={handleBookClassTrace} className="mt-6 lg:mt-12 xl:mt-15">
        <LeadButton
          asCustomButton
          callbackUrl={
            typeof window !== 'undefined' ? window.location.href : ''
          }
          traceName="O_WallOfLove_CaseSection_CTAClick">
          <Button
            size="large"
            className="h-[52px] px-[30px] text-[20px] font-bold lg:h-[55px] lg:px-12 lg:text-[18px] xl:h-[77px] xl:px-[64px] xl:text-[24px]">
            {t('案例-按钮')}
          </Button>
        </LeadButton>
      </div>
    </section>
  );
};

export default CaseSection;
